/* @ngInject */

export default (
  $rootScope,
  helperService,
  helperPdfService,
  dateTimeService,
  PaymentFrequency
) => {
  /**
   * Prepare asset finance finance proposal.
   *
   * @param {Object} financeProposal
   * @param {Object} options
   * @param {String} date
   * @param {Boolean} date
   *
   * @returns {Array}
   */
  const prepareAssetFinanceFinanceProposalDetails = ({
    financeProposal,
    options,
    date,
    isCommercial
  }) => {
    const content = [];
    if (financeProposal) {
      const now = moment.utc().tz($rootScope.CurrentTimeZone()).format('X');
      const settlementDate = date
        ? dateTimeService.getFormattedDate(date)
        : dateTimeService.getFormattedDate(now);
      content.push({
        text: '',
        margin: [0, 20, 0, 0]
      });
      content.push(
        helperPdfService.createHeader({
          title: 'FINANCE PROPOSAL'
        })
      );
      let count = 0;
      const amortizationScheduleCounts = [];
      for (let i = 0; i < financeProposal.amortizationSchedules.length; i += 1) {
        if (count === 0) {
          amortizationScheduleCounts.push({
            repaymentDate: financeProposal.amortizationSchedules[i].getPaymentDate(),
            numberOfPayments: count + 1,
            repayment: financeProposal.amortizationSchedules[i].repayment
          });
        }
        if (i + 1 <= financeProposal.amortizationSchedules.length - 1) {
          if (
            financeProposal.amortizationSchedules[i].repayment !==
            financeProposal.amortizationSchedules[i + 1].repayment
          ) {
            amortizationScheduleCounts[amortizationScheduleCounts.length - 1].numberOfPayments =
              count + 1;
            amortizationScheduleCounts[amortizationScheduleCounts.length - 1].repayment =
              financeProposal.amortizationSchedules[i].repayment;
            count = 0;
          } else {
            count += 1;
          }
        } else {
          amortizationScheduleCounts[amortizationScheduleCounts.length - 1].numberOfPayments =
            count + 1;
        }
      }
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [['', '', '', '']]
        },
        layout: 'noBorders'
      });

      content[content.length - 1].table.body[0][0] = {
        text: [
          { text: 'Net amount financed\n', style: 'label' },
          {
            text: financeProposal?.netAmountFinanced.formatWithCurrency(
              $rootScope.CurrentCurrency(),
              2
            ),
            style: 'value'
          }
        ]
      };
      content[content.length - 1].table.body[0][1] = {
        text: [
          { text: 'Term (months)\n', style: 'label' },
          {
            text: helperService.getTitleOfSelectedTermInMonths({
              term: financeProposal?.term
            }),
            style: 'value'
          }
        ]
      };
      if (options?.withoutFields?.indexOf('estimatedBorrowerRate') > -1) {
        content[content.length - 1].table.body[0][2] = {
          text: [
            {
              text: `${
                financeProposal?.productType === 2 ||
                financeProposal?.productType === 3 ||
                financeProposal?.productType === 7
                  ? 'Rental'
                  : 'Repayment'
              }\n`,
              style: 'label'
            },
            {
              text:
                amortizationScheduleCounts.length > 1
                  ? 'Structured'
                  : financeProposal?.estimatedRepayment.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      2
                    ),
              style: 'value'
            }
          ]
        };
      } else {
        content[content.length - 1].table.body[0][2] = {
          text: [
            { text: 'Interest rate\n', style: 'label' },
            {
              text: `${financeProposal?.estimatedBorrowerRate.toFixed(2)}%`,
              style: 'value'
            }
          ]
        };
        content[content.length - 1].table.body[0][3] = {
          text: [
            {
              text: `${
                financeProposal?.productType === 2 ||
                financeProposal?.productType === 3 ||
                financeProposal?.productType === 7
                  ? 'Rental'
                  : 'Repayment'
              }\n`,
              style: 'label'
            },
            {
              text:
                amortizationScheduleCounts.length > 1
                  ? 'Structured'
                  : financeProposal?.estimatedRepayment.formatWithCurrency(
                      $rootScope.CurrentCurrency(),
                      2
                    ),
              style: 'value'
            }
          ]
        };
      }

      if (
        financeProposal?.productType === 2 ||
        financeProposal?.productType === 3 ||
        financeProposal?.productType === 7
      ) {
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                {
                  text: [
                    { text: 'GST\n', style: 'label' },
                    {
                      text: financeProposal?.gstOfEstimatedRepayment.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        2
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Total rental\n', style: 'label' },
                    {
                      text: financeProposal?.estimatedRepaymentWithGst.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        2
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Product type\n', style: 'label' },
                    {
                      text: helperService.getAssetFinanceProductType(
                        financeProposal?.productType
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Balloon percent\n', style: 'label' },
                    {
                      text: `${financeProposal?.balloonPercent.toFixed(2)}%` || '-',
                      style: 'value'
                    }
                  ]
                }
              ],
              [
                {
                  text: [
                    { text: 'Balloon value\n', style: 'label' },
                    {
                      text: financeProposal?.balloonValue.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        2
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Payment structure\n', style: 'label' },
                    {
                      text: financeProposal?.payments ? 'Advance' : 'Arrears',
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Payment frequency\n', style: 'label' },
                    {
                      text:
                        financeProposal?.paymentFrequency?.name !== PaymentFrequency.none.name
                          ? financeProposal?.paymentFrequency?.title || '-'
                          : '-',
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Settlement date\n', style: 'label' },
                    {
                      text: settlementDate,
                      style: 'value'
                    }
                  ]
                }
              ]
            ]
          },
          layout: 'noBorders'
        });
      } else {
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                {
                  text: [
                    { text: 'Product type\n', style: 'label' },
                    {
                      text: helperService.getAssetFinanceProductType(
                        financeProposal?.productType
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Balloon percent\n', style: 'label' },
                    {
                      text: `${financeProposal?.balloonPercent?.toFixed(2)}%` || '-',
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Balloon value\n', style: 'label' },
                    {
                      text: financeProposal?.balloonValue?.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        2
                      ),
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Broker origination fee\n', style: 'label' },
                    {
                      text: isCommercial
                        ? financeProposal?.brokerOriginationFee?.formatWithCurrency(
                            $rootScope.CurrentCurrency(),
                            2
                          )
                        : financeProposal?.brokerFee?.formatWithCurrency(
                            $rootScope.CurrentCurrency(),
                            2
                          ),
                      style: 'value'
                    }
                  ]
                }
              ],
              [
                {
                  text: [
                    { text: 'Payment structure\n', style: 'label' },
                    {
                      text: financeProposal?.payments ? 'Advance' : 'Arrears',
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Payment frequency\n', style: 'label' },
                    {
                      text:
                        financeProposal?.paymentFrequency?.name !== PaymentFrequency.none.name
                          ? financeProposal?.paymentFrequency?.title || '-'
                          : '-',
                      style: 'value'
                    }
                  ]
                },
                {
                  text: [
                    { text: 'Settlement date\n', style: 'label' },
                    {
                      text: settlementDate,
                      style: 'value'
                    }
                  ]
                },
                ''
              ]
            ]
          },
          layout: 'noBorders'
        });
      }
      if (amortizationScheduleCounts.length > 1) {
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                { text: 'Date', style: 'label' },
                { text: '# Payments', style: 'label' },
                {
                  text: `${
                    financeProposal?.productType === 2 ||
                    financeProposal?.productType === 3 ||
                    financeProposal?.productType === 7
                      ? 'Rental'
                      : 'Repayment'
                  }\n`,
                  style: 'label'
                },
                ''
              ]
            ]
          },
          layout: 'noBorders'
        });
        const aSLen = amortizationScheduleCounts.length;
        for (let a = 0; a < aSLen; a += 1) {
          const amortizationScheduleCount = amortizationScheduleCounts[a];
          content[content.length - 1].table.body.push(['', '', '', '']);
          content[content.length - 1].table.body[
            content[content.length - 1].table.body.length - 1
          ][0] = {
            text: amortizationScheduleCount.repaymentDate,
            style: 'value'
          };
          content[content.length - 1].table.body[
            content[content.length - 1].table.body.length - 1
          ][1] = {
            text: amortizationScheduleCount.numberOfPayments,
            style: 'value'
          };
          content[content.length - 1].table.body[
            content[content.length - 1].table.body.length - 1
          ][2] = {
            text: amortizationScheduleCount.repayment.formatWithCurrency(
              $rootScope.CurrentCurrency(),
              2
            ),
            style: 'value'
          };
        }
      }
      if (financeProposal?.associatedFees?.length) {
        content.push(
          helperPdfService.createSubHeader({
            title: 'ASSOCIATED FEES - INCLUDING GST/VAT WHERE APPLICABLE'
          })
        );
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [['', '', '', '']]
          },
          layout: 'noBorders'
        });
        let totalAssociatedFee = 0;
        const len = financeProposal.associatedFees.length;
        for (let f = 0; f < len; f += 1) {
          if (f >= 1) {
            content[content.length - 1].table.body[f] = ['', '', '', ''];
          }
          const associatedFee = financeProposal.associatedFees[f];
          content[content.length - 1].table.body[f][0] = {
            text: [
              { text: 'Name\n', style: 'label' },
              {
                text: associatedFee?.name || '-',
                style: 'value'
              }
            ]
          };
          content[content.length - 1].table.body[f][2] = {
            text: [
              { text: 'Capitalise\n', style: 'label' },
              {
                text: associatedFee?.isCapitalised ? 'Yes' : 'No',
                style: 'value'
              }
            ]
          };
          content[content.length - 1].table.body[f][3] = {
            text: [
              { text: 'Amount\n', style: 'label' },
              {
                text: associatedFee?.amountWithGst.formatWithCurrency(
                  $rootScope.CurrentCurrency(),
                  2
                ),
                style: 'value'
              }
            ]
          };
          totalAssociatedFee += associatedFee?.amountWithGst || 0;
        }
        content.push(
          helperPdfService.createSubHeader({
            title: ''
          })
        );
        content.push({
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                '',
                '',
                '',
                {
                  text: [
                    { text: 'Total\n', style: 'label' },
                    {
                      text: totalAssociatedFee.formatWithCurrency(
                        $rootScope.CurrentCurrency(),
                        2
                      ),
                      style: 'value'
                    }
                  ]
                }
              ]
            ]
          },
          layout: 'noBorders'
        });
      }
    }
    return content;
  };

  return {
    prepareAssetFinanceFinanceProposalDetails
  };
};
